import { Injectable, signal } from '@angular/core';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastAction {
  /** Already-translated label, same contract as `message`. */
  label: string;
  run?: () => void;
}

export interface ToastOptions {
  /** Already-translated title; falls back to the per-type default. */
  title?: string;
  /** Auto-dismiss delay in ms; 0 keeps the toast until closed manually. */
  duration?: number;
  actions?: ToastAction[];
}

export interface ToastItem {
  id: number;
  type: ToastType;
  title?: string;
  message: string;
  duration: number;
  actions: ToastAction[];
  /** Set while the leave animation plays, just before removal. */
  leaving: boolean;
}

const DEFAULT_DURATION_MS = 5000;
/** Must match the toast-out animation duration in toast.component.css. */
const LEAVE_ANIMATION_MS = 200;
const MAX_VISIBLE = 5;

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private nextId = 0;
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();

  /** Active toasts, oldest first. Rendered by the global Toast component. */
  readonly toasts = signal<ToastItem[]>([]);

  show(message: string, type: ToastType = 'success', options: ToastOptions = {}): number {
    const id = this.nextId++;
    const duration = options.duration ?? DEFAULT_DURATION_MS;

    this.toasts.update((list) => {
      const next: ToastItem[] = [
        ...list,
        {
          id,
          type,
          message,
          title: options.title,
          duration,
          actions: options.actions ?? [],
          leaving: false,
        },
      ];
      // Cap the stack so an event burst can't fill the screen; oldest go first.
      const overflow = next.length - MAX_VISIBLE;
      if (overflow > 0) {
        next.slice(0, overflow).forEach((toast) => this.clearTimer(toast.id));
      }
      return overflow > 0 ? next.slice(overflow) : next;
    });

    if (duration > 0) {
      this.timers.set(
        id,
        setTimeout(() => this.dismiss(id), duration),
      );
    }
    return id;
  }

  dismiss(id: number): void {
    this.clearTimer(id);
    // Two-step removal: flag as leaving so the exit animation plays, then drop.
    this.toasts.update((list) =>
      list.map((toast) => (toast.id === id ? { ...toast, leaving: true } : toast)),
    );
    this.timers.set(
      id,
      setTimeout(() => {
        this.timers.delete(id);
        this.toasts.update((list) => list.filter((toast) => toast.id !== id));
      }, LEAVE_ANIMATION_MS),
    );
  }

  private clearTimer(id: number): void {
    const timer = this.timers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
  }
}
